import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ViewFilled16, ViewOffFilled16, WarningAltFilled20 } from '@carbon/icons-react';
import { OktaContext } from '@okta/okta-react';
import { useLocationPathname, useLocationSearch, useNavigate } from '@rocketlane/rocket-router';
// eslint-disable-next-line no-restricted-imports
import { Col, Row } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI } from 'api/account';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { BackgroundColor, Input, InputBorder, InputFocus } from 'components/atoms/input/input';
import { Button } from 'components/facade/button';
import { BodyShort01, Text01 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { useIsSamlMandatory } from '../saml';
import { ForgotPassword } from './forgot-password';
import { SsoSamlButton } from './sso-saml-button';
import { useCognitoAuth } from './use-cognito-auth';
var PasswordInputType = /*#__PURE__*/ function(PasswordInputType) {
    PasswordInputType["PASSWORD"] = "password";
    PasswordInputType["TEXT"] = "text";
    return PasswordInputType;
}(PasswordInputType || {});
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  & button {
    height: 50px;
  }
  margin-bottom: var(--spacing-05);
`;
const WidgetContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-form-item {
    margin-bottom: 0 !important;
  }

  .ant-form-item:nth-child(1) {
    padding-bottom: var(--spacing-03) !important;
  }

  .ant-form-item-control-input-content input {
    width: 100%;
    padding: 0 var(--spacing-05);
    ${InputBorder};
    ${BodyShort01};
    ${Text01};
    ${BackgroundColor};
    height: var(--input-height-lg);

    &.focused-input {
      ${InputFocus};
    }
  }
`;
const SignInButton = styled(Button)`
  text-align: left;
  border-radius: 0;
  border-color: var(--blue-60);
  background: var(--blue-60);
  color: var(--white-100);
  height: var(--input-height-lg);
  margin-bottom: var(--spacing-03);
`;
const Extras = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-02);
  margin-bottom: var(--spacing-07);
`;
const StyledCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px !important;
  font-weight: 400 !important;
  font-family: var(--font-family) !important;
`;
const AlertWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: var(--spacing-05) 0;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    fill: var(--red-50);
  }
`;
const PasswordContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;
const ShowPasswordContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: var(--spacing-05);
  color: var(--gray-30);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const errorMessages = [
    'UserMigration failed with error User account already exists.',
    'UserMigration failed with error User migrated successfully.'
];
const lambdaErrorMessages = [
    'UserMigration failed with error Incorrect username or password.'
];
export const CustomSignInWidget = ({ data })=>{
    var _data_okta_idps;
    var _data_okta, _useContext;
    const translate = useTranslate();
    const [form] = Form.useForm();
    const pathname = useLocationPathname();
    const search = useLocationSearch();
    const navigate = useNavigate();
    const isSamlMandate = useIsSamlMandatory();
    const [checked, setChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [notAnErrorBuErrorMessage, setNotAnErrorBuErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [passwordInputType, setPasswordInputType] = useState("password");
    const isCustomAuthentication = data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.customAuthentication;
    const [showPassword, setShowPassword] = useState(false);
    const isSamlEnabled = useMemo(()=>{
        var _ref;
        return !!((_ref = data === null || data === void 0 ? void 0 : data.okta.idps) !== null && _ref !== void 0 ? _ref : []).filter((idp)=>idp.enabled).length;
    }, [
        data === null || data === void 0 ? void 0 : data.okta.idps
    ]);
    const onClick = ()=>{
        setErrorMessage('');
        form.validateFields().then((values)=>{
            var _data_okta, _data_okta1, _data_okta2;
            const { email, password } = values.information[0];
            if (!email || !password) {
                setLoading(false);
                return;
            }
            setLoading(true);
            AccountAPI.loginWithCognito({
                userName: email,
                password,
                clientId: data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.clientId,
                userNamesuffix: data === null || data === void 0 ? void 0 : (_data_okta1 = data.okta) === null || _data_okta1 === void 0 ? void 0 : _data_okta1.userName,
                issuerDomain: data === null || data === void 0 ? void 0 : (_data_okta2 = data.okta) === null || _data_okta2 === void 0 ? void 0 : _data_okta2.issuerDomain
            }, isCustomAuthentication)// @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            .then((res)=>{
                setLoading(false);
                const { token } = res;
                navigate(`${pathname}?token=${token}&cognito=${true}`);
            }).catch((error)=>{
                var _error_response_data_errors_, _error_response_data, _error_response;
                if (errorMessages.includes(error === null || error === void 0 ? void 0 : error.message)) {
                    setNotAnErrorBuErrorMessage(translate('components.login.something_went_wrong_try_again'));
                } else if (lambdaErrorMessages.includes(error === null || error === void 0 ? void 0 : error.message)) {
                    setErrorMessage(translate('components.login.incorrect_username_or_password'));
                } else if (error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : (_error_response_data = _error_response.data) === null || _error_response_data === void 0 ? void 0 : (_error_response_data_errors_ = _error_response_data.errors[0]) === null || _error_response_data_errors_ === void 0 ? void 0 : _error_response_data_errors_.errorMessage) {
                    setErrorMessage(error.response.data.errors[0].errorMessage);
                } else if (error === null || error === void 0 ? void 0 : error.message) {
                    setErrorMessage(error.message);
                }
                setLoading(false);
            });
        });
    };
    const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
        data
    });
    const oktaAuth = (_useContext = useContext(OktaContext)) === null || _useContext === void 0 ? void 0 : _useContext.oktaAuth;
    const params = new URLSearchParams(search);
    const partnerParam = params.get('partner');
    return /*#__PURE__*/ _jsx(WidgetContainer, {
        children: isSamlMandate && isSamlEnabled && !partnerParam ? /*#__PURE__*/ _jsx(ButtonContainer, {
            children: ((_data_okta_idps = data.okta.idps) !== null && _data_okta_idps !== void 0 ? _data_okta_idps : []).filter((idp)=>idp.enabled).map((idp)=>{
                return /*#__PURE__*/ _jsx(SsoSamlButton, {
                    idp: idp,
                    googleSSOHandler: googleSSOHandler,
                    samlFlowHandler: samlFlowHandler,
                    isCognitoEnabled: true,
                    idpId: idp.idpId,
                    oktaAuth: oktaAuth
                }, idp === null || idp === void 0 ? void 0 : idp.idpId);
            })
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                errorMessage && /*#__PURE__*/ _jsxs(AlertWrapper, {
                    color: "red",
                    children: [
                        /*#__PURE__*/ _jsx(WarningAltFilled20, {}),
                        errorMessage
                    ]
                }),
                notAnErrorBuErrorMessage && /*#__PURE__*/ _jsxs(AlertWrapper, {
                    color: "blue",
                    children: [
                        /*#__PURE__*/ _jsx(WarningAltFilled20, {}),
                        notAnErrorBuErrorMessage
                    ]
                }),
                /*#__PURE__*/ _jsx(Form, {
                    form: form,
                    layout: "vertical",
                    initialValues: {
                        information: [
                            {
                                email: '',
                                password: ''
                            }
                        ]
                    },
                    onKeyDownCapture: (e)=>{
                        if (e.key === 'Enter' && !loading) {
                            form.validateFields().then(()=>{
                                const { email, password } = form.getFieldValue('information')[0];
                                if (email && password) {
                                    e.preventDefault();
                                    onClick();
                                }
                            });
                        }
                    },
                    children: /*#__PURE__*/ _jsx(Form.List, {
                        name: "information",
                        children: (fields)=>{
                            return /*#__PURE__*/ _jsx(_Fragment, {
                                children: fields.map((field)=>{
                                    var _field_fieldKey, _field_fieldKey1;
                                    return /*#__PURE__*/ _jsxs(Row, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Col, {
                                                span: 24,
                                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                                    id: "email",
                                                    ...field,
                                                    name: [
                                                        field.name,
                                                        'email'
                                                    ],
                                                    fieldKey: [
                                                        (_field_fieldKey = field.fieldKey) !== null && _field_fieldKey !== void 0 ? _field_fieldKey : '',
                                                        'email'
                                                    ],
                                                    label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                        id: "email"
                                                    }),
                                                    rules: [
                                                        {
                                                            required: true,
                                                            message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                                id: "please_enter_email"
                                                            })
                                                        }
                                                    ],
                                                    children: /*#__PURE__*/ _jsx(Input, {
                                                        placeholder: translate('email'),
                                                        type: "email",
                                                        name: "email",
                                                        autoComplete: "on"
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Col, {
                                                span: 24,
                                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                                    id: "password",
                                                    ...field,
                                                    name: [
                                                        field.name,
                                                        'password'
                                                    ],
                                                    fieldKey: [
                                                        (_field_fieldKey1 = field.fieldKey) !== null && _field_fieldKey1 !== void 0 ? _field_fieldKey1 : '',
                                                        'password'
                                                    ],
                                                    label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                        id: "password"
                                                    }),
                                                    rules: [
                                                        {
                                                            required: true,
                                                            message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                                id: "please_enter_password"
                                                            })
                                                        }
                                                    ],
                                                    children: /*#__PURE__*/ _jsxs(PasswordContainer, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Input, {
                                                                placeholder: translate('password'),
                                                                type: passwordInputType,
                                                                name: "password"
                                                            }),
                                                            /*#__PURE__*/ _jsx(ShowPasswordContainer, {
                                                                onClick: ()=>{
                                                                    setShowPassword(!showPassword);
                                                                    setPasswordInputType(showPassword ? "password" : "text");
                                                                },
                                                                children: !showPassword ? /*#__PURE__*/ _jsx(ViewFilled16, {}) : /*#__PURE__*/ _jsx(ViewOffFilled16, {})
                                                            })
                                                        ]
                                                    })
                                                })
                                            })
                                        ]
                                    }, field === null || field === void 0 ? void 0 : field.key);
                                })
                            });
                        }
                    })
                }),
                /*#__PURE__*/ _jsxs(Extras, {
                    children: [
                        /*#__PURE__*/ _jsx(StyledCheckbox, {
                            checked: checked,
                            onChange: (event)=>{
                                setChecked(event.target.checked);
                            },
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "remember_me"
                            })
                        }),
                        /*#__PURE__*/ _jsx(ForgotPassword, {
                            isSamlMandate: isSamlMandate
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SignInButton, {
                    loading: loading,
                    onClick: onClick,
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "magic_link.otp.sigin.button.text"
                    })
                })
            ]
        })
    });
};
